import type { SupabaseClient, User } from '@supabase/supabase-js';
import { usernameSchema } from '@garagegymbros/contracts';

/** Returns true when onboarding needs a new name after verification, including a claim race. */
export async function completeSignupProfile(
  client: SupabaseClient,
  user: Pick<User, 'id' | 'email_confirmed_at' | 'user_metadata'>,
): Promise<boolean> {
  if (!user.email_confirmed_at) return false;
  const { data: profile, error } = await client
    .from('profiles')
    .select('username,display_name')
    .eq('id', user.id)
    .maybeSingle();
  if (error) throw new Error('Your profile details could not be loaded.');
  const desiredName = String(user.user_metadata?.display_name ?? '')
    .trim()
    .slice(0, 80);
  const desiredUsername = String(user.user_metadata?.username ?? '');
  if (profile?.display_name !== 'New member' || !desiredName) return false;
  const candidate = usernameSchema.safeParse(desiredUsername);
  const result = await client
    .from('profiles')
    .update({
      display_name: desiredName,
      ...(candidate.success ? { username: candidate.data } : {}),
    })
    .eq('id', user.id);
  if (result.error) {
    // Someone may claim the name between registration and email verification.
    // Keep the verified account usable and take its member to name selection.
    await client.from('profiles').update({ display_name: desiredName }).eq('id', user.id);
    return !!desiredUsername;
  }
  return !!desiredUsername && !candidate.success;
}
